import { useState } from "react";
import toast from "react-hot-toast";
import { createAdminNotification } from "../../utils/notificationService";
import { BiBus, BiTrash, BiPlus } from "react-icons/bi";
import { IoCloseSharp } from "react-icons/io5";
import { useSelector } from "react-redux";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import {
  trackFedexShipmentApi,
  trackUpsShipmentApi,
} from "../../api/TrackingApi";
import { saveData, uniqueID } from "../../firebase/utility";
import detectCarrier from "../../utility";
import GeneralButton from "../button/GeneralButton";
import FormInput from "../formInput/FormInput";

const AddTrackingModal = ({
  modal,
  toggle,
  line_items,
  orderData,
  index,
  product,
  tracking,
  setTracking,
  refreshSaleOrderDetails,
}) => {
  const [lineItems, setLineItems] = useState(line_items ?? []);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const [trackingsList, setTrackingsList] = useState([
    {
      trackingNumber: tracking?.trackingNumber || "",
      company: tracking?.company || "",
      quantity: tracking?.quantity || product?.quantity || 0,
      trackingType: tracking?.trackingType || "",
      otherCompany: tracking?.otherCompany || "",
    }
  ]);

  const handleAddRow = () => {
    setTrackingsList([
      ...trackingsList,
      {
        trackingNumber: "",
        company: "",
        quantity: product?.quantity || 0,
        trackingType: "",
        otherCompany: "",
      }
    ]);
  };

  const handleRemoveRow = (idx) => {
    const list = [...trackingsList];
    list.splice(idx, 1);
    setTrackingsList(list);
  };

  const handleTrackingChange = (idx, key, value) => {
    const list = [...trackingsList];
    list[idx][key] = value;
    setTrackingsList(list);
  };

  const saveTrackingToFirebase = async (trackingItem, trackingData, carrier) => {
    const newLabel = {
      trackingData: trackingData || trackingItem.trackingNumber,
      type: carrier.toLowerCase(),
      trackingNumber: trackingItem.trackingNumber,
      trackingType: trackingItem.trackingType ?? "outbound",
    };
    let finalObj = {
      _id: uniqueID(),
      orderId: orderData?._id,
      labelData: newLabel,
      lineItemDetails: [
        {
          quantity: trackingItem.quantity ?? product?.quantity,
          price: product?.price,
          _id: product?._id,
          sku: product?.sku,
        },
      ],
      createdAt: Date.parse(new Date()),
      labelType: newLabel?.type,
    };
    return saveData("Trackings", finalObj?._id, finalObj)?.then((result) => {
      return !!result;
    });
  };

  const processSingleTracking = (trackingItem) => {
    return new Promise(async (resolve) => {
      let userInfo = {
        username: user?.username ?? "",
        email: user?.email ?? "",
        role: user?.role ?? "",
        profileImage: user?.profileImage ?? "",
        phone: user?.phone ?? "",
        address: user?.address ?? "",
        _id: user?._id ?? "",
      };

      if (!trackingItem?.quantity || trackingItem?.quantity === 0 || !trackingItem?.trackingType) {
        toast.error(`Please ensure all required fields are filled out for ${trackingItem?.trackingNumber || 'entry'} to proceed.`);
        return resolve(false);
      }

      const detectedCarrier = detectCarrier(trackingItem?.trackingNumber);
      const carrier = detectedCarrier !== "UNKNOWN" ? detectedCarrier : trackingItem?.company;
      if (!carrier || carrier === "UNKNOWN") {
        toast.error(`Unable to detect carrier for ${trackingItem?.trackingNumber}. Please select a valid company.`);
        return resolve(false);
      }
      trackingItem.company = carrier;

      if (trackingItem?.company === "UPS") {
        await trackUpsShipmentApi(trackingItem?.trackingNumber, (res) => {
          if (res?.status === 200 && !res?.data?.trackResponse?.shipment[0]?.warnings) {
            const newLabel = {
              trackingData: res?.data,
              type: "ups",
              trackingNumber: trackingItem?.trackingNumber,
              trackingType: trackingItem?.trackingType ?? "",
            };
            let finalObj = {
              _id: uniqueID(),
              orderId: orderData?._id,
              createdAt: Date.parse(new Date()),
              labelData: newLabel,
              lineItemDetails: [
                {
                  quantity: trackingItem?.quantity ?? product?.quantity,
                  price: product?.price,
                  _id: product?._id,
                  sku: product?.sku,
                },
              ],
              labelType: newLabel?.type,
              createdBy: userInfo ?? {},
            };
            saveData("Trackings", finalObj?._id, finalObj)?.then((result) => {
              resolve(!!result);
            });
          } else {
            toast.error(res?.data?.trackResponse?.shipment[0]?.warnings[0]?.message);
            resolve(false);
          }
        });
      } else if (trackingItem?.company === "FEDEX") {
        await trackFedexShipmentApi(trackingItem?.trackingNumber, (res) => {
          if (res?.status === 200) {
            if (res?.data?.output?.completeTrackResults[0]?.trackResults[0]?.error) {
              const errorCode = res?.data?.output?.completeTrackResults[0]?.trackResults[0]?.error?.code;
              if (errorCode === "TRACKING.TRACKINGNUMBER.NOTFOUND") {
                const manualTrackingData = {
                  trackResults: [
                    {
                      additionalTrackingInfo: {
                        hasAssociatedShipments: false,
                        nickname: "",
                        packageIdentifiers: [
                          {
                            carrierCode: "",
                            trackingNumberUniqueId: "",
                            type: "CUSTOMER_REFERENCE",
                            values: ["accounting@techmicrousa.com"],
                          },
                        ],
                      },
                      availableImages: [],
                      dateAndTimes: [
                        {
                          dateTime: new Date().toISOString(),
                          type: "ANTICIPATED_TENDER",
                        },
                      ],
                      deliveryDetails: {
                        deliveryAttempts: "0",
                      },
                      destinationLocation: {
                        locationContactAndAddress: {
                          address: {
                            city: orderData?.shippingAddress?.city || "JONESBORO",
                            countryCode: "US",
                            countryName: "United States",
                            residential: false,
                            stateOrProvinceCode: orderData?.shippingAddress?.state || "AR",
                          },
                        },
                        locationType: "FEDEX_HOME_DELIVERY_STATION",
                      },
                      lastUpdatedDestinationAddress: {
                        city: orderData?.shippingAddress?.city || "JONESBORO",
                        countryCode: "US",
                        countryName: "United States",
                        residential: false,
                        stateOrProvinceCode: orderData?.shippingAddress?.state || "AR",
                      },
                      latestStatusDetail: {
                        code: "OC",
                        derivedCode: "OC",
                        description: "Shipment information sent to FedEx",
                        scanLocation: {
                          city: "BOLINGBROOK",
                          countryCode: "US",
                          countryName: "United States",
                          residential: false,
                          stateOrProvinceCode: "IL",
                        },
                        statusByLocale: "Label created",
                      },
                      packageDetails: {
                        count: "1",
                        weightAndDimensions: {
                          weight: [
                            {
                              unit: "LB",
                              value: product?.weight || "1.0",
                            },
                          ],
                        },
                      },
                      serviceDetail: {
                        description: "",
                        shortDescription: "",
                        type: "",
                      },
                      shipmentDetails: {
                        possessionStatus: false,
                      },
                      shipperInformation: {
                        address: {
                          city: "BOLINGBROOK",
                          countryCode: "US",
                          countryName: "United States",
                          residential: false,
                          stateOrProvinceCode: "IL",
                        },
                      },
                    },
                  ],
                  trackingNumber: trackingItem?.trackingNumber,
                  trackingType: trackingItem?.trackingType || "Direct Ship",
                  type: "fedex",
                  createdBy: userInfo ?? {},
                };
                console.log("FedEx Tracking Not Found Error. Submitted Data:", {
                  trackingNumber: trackingItem?.trackingNumber,
                  orderId: orderData?._id,
                  carrier: "FEDEX",
                  trackingData: manualTrackingData,
                });
                saveTrackingToFirebase(trackingItem, manualTrackingData, "FEDEX").then((res) => {
                  toast.success(`Tracking number ${trackingItem?.trackingNumber} saved and will be updated later`);
                  resolve(res);
                });
                return;
              }
              toast.error(res?.data?.output?.completeTrackResults[0]?.trackResults[0]?.error?.message);
              return resolve(false);
            }
            const newLabel = {
              trackingData: res?.data?.output?.completeTrackResults[0],
              type: "fedex",
              trackingNumber: trackingItem?.trackingNumber,
              trackingType: trackingItem?.trackingType ?? "",
            };
            let finalObj = {
              _id: uniqueID(),
              orderId: orderData?._id,
              createdAt: Date.parse(new Date()),
              labelData: newLabel,
              lineItemDetails: [
                {
                  quantity: trackingItem?.quantity ?? product?.quantity,
                  price: product?.price,
                  _id: product?._id,
                  sku: product?.sku,
                },
              ],
              labelType: newLabel?.type,
              createdBy: userInfo ?? {},
            };
            saveData("Trackings", finalObj?._id, finalObj)?.then((result) => resolve(!!result));
          } else {
            toast.error(res?.message);
            resolve(false);
          }
        });
      } else if (trackingItem?.company === "USPS") {
        const newLabel = {
          trackingData: trackingItem?.trackingNumber,
          trackingNumber: trackingItem?.trackingNumber,
          type: "usps",
          trackingType: trackingItem?.trackingType ?? "",
        };
        let finalObj = {
          _id: uniqueID(),
          orderId: orderData?._id,
          labelData: newLabel,
          lineItemDetails: [
            {
              quantity: trackingItem?.quantity ?? product?.quantity,
              price: product?.price,
              _id: product?._id,
              sku: product?.sku,
            },
          ],
          createdAt: Date.parse(new Date()),
          labelType: newLabel?.type,
          createdBy: userInfo ?? {},
        };
        saveData("Trackings", finalObj?._id, finalObj)?.then((result) => resolve(!!result));
      } else {
        const newLabel = {
          trackingData: trackingItem?.trackingNumber,
          trackingNumber: trackingItem?.trackingNumber,
          trackingType: trackingItem?.trackingType,
          type: "others",
          company: trackingItem?.otherCompany,
        };
        let finalObj = {
          _id: uniqueID(),
          orderId: orderData?._id,
          createdAt: Date.parse(new Date()),
          labelData: newLabel,
          lineItemDetails: [
            {
              quantity: trackingItem?.quantity ?? product?.quantity,
              price: product?.price,
              _id: product?._id,
              sku: product?.sku,
            },
          ],
          labelType: newLabel?.type,
          createdBy: userInfo ?? {},
        };
        saveData("Trackings", finalObj?._id, finalObj)?.then((result) => resolve(!!result));
      }
    });
  };

  const handleTracking = async () => {
    setLoading(true);
    let allSuccess = true;

    // Validate all items before processing
    for (let i = 0; i < trackingsList.length; i++) {
      let t = trackingsList[i];
      if (!t.quantity || t.quantity === 0 || !t.trackingNumber || !t.company || !t.trackingType || (t.company === "OTHERS" && !t.otherCompany)) {
        toast.error(`Please ensure all required fields are filled out for row ${i + 1} to proceed.`);
        setLoading(false);
        return;
      }
    }

    for (let t of trackingsList) {
      let success = await processSingleTracking(t);
      if (!success) {
        allSuccess = false;
      }
    }

    if (allSuccess) {
      toast.success("Successfully added tracking(s)");
      const targetOrderId = orderData?.id || orderData?._id || "";
      const orderNumberDisplay = orderData?.number || orderData?.id || "Order";
      createAdminNotification({
        title: "Tracking Added",
        message: `Tracking added for Order #${orderNumberDisplay} by ${user?.username || "Admin"}`,
        category: "order",
        entityId: String(orderNumberDisplay),
        targetUrl: targetOrderId ? `/admin/sale-order-details/${targetOrderId}` : "/admin/sale-orders",
        actor: user,
      }).catch(console.error);
      toggle();
      refreshSaleOrderDetails();
      if (setTracking) {
        setTracking({
          trackingNumber: "",
          company: "",
          quantity: 0,
          trackingType: "",
        });
      }
    }
    setLoading(false);
  };

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      centered
      size="xl"
      onClosed={() => {
        if (setTracking) setTracking(null);
      }}
    >
      <ModalBody className="p-0 border-0">
        <div className="modern-modal-header d-flex justify-content-between align-items-center p-4 border-bottom bg-light rounded-top">
          <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
            <BiBus className="text-primary" size={20} /> Add Trackings
          </h5>
          <IoCloseSharp
            size={24}
            className="text-muted pointer hover-opacity-75"
            onClick={toggle}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div className="p-4" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <div className="table-responsive border rounded-3 shadow-sm">
            <table className="table table-sm table-bordered table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
              <thead className="bg-light">
                <tr>
                  <th className="py-2 text-center text-muted text-uppercase" style={{ width: '60px', fontSize: '0.75rem' }}>Qty</th>
                  <th className="py-2 text-muted text-uppercase" style={{ minWidth: '150px', fontSize: '0.75rem' }}>Tracking #</th>
                  <th className="py-2 text-muted text-uppercase" style={{ width: '150px', fontSize: '0.75rem' }}>Company</th>
                  {trackingsList.some(t => t.company === "OTHERS") && (
                    <th className="py-2 text-muted text-uppercase" style={{ width: '150px', fontSize: '0.75rem' }}>Other Company</th>
                  )}
                  <th className="py-2 text-muted text-uppercase" style={{ width: '180px', fontSize: '0.75rem' }}>Tracking Type</th>
                  <th className="py-2 text-center text-muted text-uppercase" style={{ width: '80px', fontSize: '0.75rem' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {trackingsList.map((t, idx) => (
                  <tr key={idx} className="bg-white">
                    <td className="p-2">
                      <input
                        type="number"
                        className="form-control form-control-sm shadow-none"
                        placeholder="0"
                        value={t.quantity}
                        onChange={(e) => handleTrackingChange(idx, "quantity", e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        className="form-control form-control-sm shadow-none"
                        placeholder="Tracking number"
                        value={t.trackingNumber}
                        onChange={(e) => handleTrackingChange(idx, "trackingNumber", e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <select
                        className="form-select form-select-sm shadow-none"
                        value={t.company}
                        onChange={(e) => handleTrackingChange(idx, "company", e.target.value)}
                      >
                        <option value="">Select...</option>
                        <option value="UPS">UPS</option>
                        <option value="USPS">USPS</option>
                        <option value="FEDEX">FEDEX</option>
                        <option value="OTHERS">OTHERS</option>
                      </select>
                    </td>
                    {trackingsList.some(anyT => anyT.company === "OTHERS") && (
                      <td className="p-2">
                        {t.company === "OTHERS" ? (
                          <input
                            type="text"
                            className="form-control form-control-sm shadow-none"
                            placeholder="Provide Company"
                            value={t.otherCompany}
                            onChange={(e) => handleTrackingChange(idx, "otherCompany", e.target.value)}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className="p-2">
                      <select
                        className="form-select form-select-sm shadow-none"
                        value={t.trackingType}
                        onChange={(e) => handleTrackingChange(idx, "trackingType", e.target.value)}
                      >
                        <option value="">Select...</option>
                        <option value="Direct Ship">Direct Ship</option>
                        <option value="Coming To Techmicro">Coming To Techmicro</option>
                      </select>
                    </td>
                    <td className="p-2 text-center">
                      <div className="d-flex justify-content-center gap-1">
                        {trackingsList.length > 1 && (
                          <button
                            className="btn btn-sm btn-outline-danger border-0 p-1 d-flex align-items-center"
                            onClick={() => handleRemoveRow(idx)}
                            title="Remove Tracking"
                          >
                            <BiTrash size={16} />
                          </button>
                        )}
                        {idx === trackingsList.length - 1 && (
                          <button
                            className="btn btn-sm btn-outline-primary border-0 p-1 d-flex align-items-center"
                            onClick={handleAddRow}
                            title="Add Tracking"
                          >
                            <BiPlus size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="p-4 bg-light border-top d-flex justify-content-end rounded-bottom">
          <GeneralButton
            title={"Cancel"}
            className={"white-button mx-2"}
            width={"180px"}
            handleClick={toggle}
          />
          <GeneralButton
            title={"Save"}
            className={"blue-button"}
            width={"180px"}
            handleClick={handleTracking}
            loading={loading}
            disabled={loading}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddTrackingModal;
